import React, { FunctionComponent, memo } from "react";
import { useTranslation } from "react-i18next";

import { usePreviewDealerOrderFromCart } from "../../../queries/usePreviewDealerOrderFromCart";
import {
  HashtagString,
  createHashStateHook,
} from "../../../stores/useHashState";
import { Dialog } from "../../../ui/Dialog/Dialog";
import { LinearProgress } from "../../../ui/LinearProgress";
import { NoData } from "../../NoData/NoData";
import { DealerOrderDisclaimer } from "./DealerOrderDisclaimer";
import { DealerOrderForm } from "./DealerOrderForm";
import { DealerOrderList } from "./DealerOrderList";
import { DealerOrderModalHeader } from "./DealerOrderModalHeader";
import { DealerOrderNotes } from "./DealerOrderNotes";
import { useNormalizedDealerOrder } from "./useNormalizedDealerOrder";

export const dealerOrderModalHash: HashtagString = "#order-now";

export const useDealerOrderModal = createHashStateHook(dealerOrderModalHash);

export const Modal: FunctionComponent = () => {
  const { t } = useTranslation();
  const [visible, { hide }] = useDealerOrderModal();
  const { data, isLoading, mutate } = usePreviewDealerOrderFromCart(
    visible,
    {},
    { disabled: !visible }
  );

  const normalizedData = useNormalizedDealerOrder(data);

  return (
    <Dialog
      className="p-4 md:p-6 lg:p-8 w-full"
      id="dealer-order-modal"
      onClose={hide}
      placement="top"
      visible={visible}
    >
      <DealerOrderModalHeader close={hide} />
      {data && (
        <>
          <DealerOrderNotes data={data} />
          <h2 className="md:text-lg font-semibold">
            {t("dealerOrderModal.itemInformation")}
          </h2>
        </>
      )}
      <div className="border border-dashed border-edge-2 rounded-tw-lg md:rounded-panel-section mt-2">
        {visible && isLoading && <LinearProgress className="h-[0.1rem] m-8" />}
        {!data && !isLoading && (
          <NoData className="m-4">{t("dealerOrderModal.noData")}</NoData>
        )}
        {data && !normalizedData && (
          <div className="px-4 md:px-8 py-4">
            {t("dealerOrderModal.errorMessage")}
          </div>
        )}
        {normalizedData && (
          <DealerOrderList data={normalizedData} revalidate={mutate} />
        )}
        {data && normalizedData && (
          <DealerOrderForm
            data={data}
            hide={hide}
            normalizedData={normalizedData}
          />
        )}
        <DealerOrderDisclaimer />
      </div>
    </Dialog>
  );
};

export const DealerOrderModal = memo(Modal);
